import type { CSSProperties } from "react";

import { cn } from "../../lib/cn";
import {
	GLASS_BORDER_DEFAULT_BORDER_WIDTH_PX,
	type GlassBorderTone,
} from "./glass-border";

/** Ring band width — matches {@link GLASS_BORDER_DEFAULT_BORDER_WIDTH_PX} and torch highlight. */
export const GLASS_BORDER_RING_WIDTH_PX = GLASS_BORDER_DEFAULT_BORDER_WIDTH_PX;

/** Host class: ring is painted on `::before` (zero extra DOM). Used by magic-torch surfaces. */
export const GLASS_BORDER_RING_HOST_CLASS = "glass-border-ring-host";

/** Element class: ring is an absolutely positioned band div ({@link GlassBorderRing}). */
export const GLASS_BORDER_RING_ELEMENT_CLASS = "glass-border-ring";

/** Mask geometry shared by host pseudo and ring element. */
export const GLASS_BORDER_RING_MASK_STYLE: CSSProperties = {
	WebkitMask:
		"linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
	WebkitMaskComposite: "xor",
	mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
	maskComposite: "exclude",
};

export interface GlassBorderRingClassNamesOptions {
	tone?: GlassBorderTone;
	disabled?: boolean;
	/** Host (`::before`) vs standalone element. */
	variant?: "host" | "element";
}

export function glassBorderRingToneClassName(
	tone: GlassBorderTone = "system",
): string {
	return `${GLASS_BORDER_RING_ELEMENT_CLASS}--tone-${tone}`;
}

export function glassBorderRingHostToneClassName(
	tone: GlassBorderTone = "system",
): string {
	return `${GLASS_BORDER_RING_HOST_CLASS}--tone-${tone}`;
}

export function glassBorderRingClassNames({
	tone = "system",
	disabled = false,
	variant = "element",
}: GlassBorderRingClassNamesOptions = {}): string {
	const base =
		variant === "host"
			? GLASS_BORDER_RING_HOST_CLASS
			: GLASS_BORDER_RING_ELEMENT_CLASS;
	const toneClass =
		variant === "host"
			? glassBorderRingHostToneClassName(tone)
			: glassBorderRingToneClassName(tone);
	return cn(base, toneClass, disabled && `${base}--disabled`);
}

function scopeGlassBorderRingSelector(
	selector: string,
	scopeSelector?: string,
): string {
	if (!scopeSelector) {
		return selector;
	}
	return `${scopeSelector} ${selector}`;
}

function scopeGlassBorderRingDarkSelector(
	selector: string,
	scopeSelector?: string,
): string {
	if (!scopeSelector) {
		return `.dark ${selector}`;
	}
	return `.dark ${scopeSelector} ${selector}`;
}

/**
 * Injected once per surface tree. Host surfaces add {@link glassBorderRingHostClassNames};
 * optional {@link GlassBorderRing} uses element classes.
 * Pass `scopeSelector` (e.g. `.magic-torch-root`) so ring styles never leak globally.
 */
export function glassBorderRingStylesheet(scopeSelector?: string): string {
	const w = GLASS_BORDER_RING_WIDTH_PX;
	const el = GLASS_BORDER_RING_ELEMENT_CLASS;
	const host = GLASS_BORDER_RING_HOST_CLASS;
	const scoped = (selector: string) =>
		scopeGlassBorderRingSelector(selector, scopeSelector);
	const scopedDark = (selector: string) =>
		scopeGlassBorderRingDarkSelector(selector, scopeSelector);

	return `
          ${scoped(`.${el}`)},
          ${scoped(`.${host}::before`)} {
            box-sizing: border-box;
            padding: ${w}px;
            -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
            -webkit-mask-composite: xor;
            mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
            mask-composite: exclude;
            pointer-events: none;
          }

          ${scoped(`.${el}`)} {
            position: absolute;
            inset: 0;
            z-index: 1;
          }

          ${scoped(`.${host}::before`)} {
            content: '';
            position: absolute;
            inset: 0;
            z-index: 1;
          }

          ${scoped(`.${el}--tone-system`)},
          ${scoped(`.${host}--tone-system::before`)} {
            background: linear-gradient(
              145deg,
              rgba(0, 0, 0, 0.14) 0%,
              rgba(0, 0, 0, 0.05) 45%,
              rgba(0, 0, 0, 0.09) 100%
            );
          }

          ${scopedDark(`.${el}--tone-system`)},
          ${scopedDark(`.${host}--tone-system::before`)} {
            background: linear-gradient(
              145deg,
              rgba(255, 255, 255, 0.72) 0%,
              rgba(255, 255, 255, 0.28) 45%,
              rgba(255, 255, 255, 0.5) 100%
            );
          }

          ${scoped(`.${el}--tone-dark`)},
          ${scoped(`.${host}--tone-dark::before`)} {
            background: linear-gradient(
              145deg,
              rgba(0, 0, 0, 0.14) 0%,
              rgba(0, 0, 0, 0.05) 45%,
              rgba(0, 0, 0, 0.09) 100%
            );
          }

          ${scoped(`.${el}--tone-light`)},
          ${scoped(`.${host}--tone-light::before`)} {
            background: linear-gradient(
              145deg,
              rgba(255, 255, 255, 0.72) 0%,
              rgba(255, 255, 255, 0.28) 45%,
              rgba(255, 255, 255, 0.5) 100%
            );
          }

          ${scoped(`.${el}--disabled`)},
          ${scoped(`.${host}--disabled::before`)} {
            background: linear-gradient(
              145deg,
              rgba(0, 0, 0, 0.08) 0%,
              rgba(0, 0, 0, 0.03) 45%,
              rgba(0, 0, 0, 0.05) 100%
            );
          }

          ${scopedDark(`.${el}--disabled`)},
          ${scopedDark(`.${host}--disabled::before`)} {
            background: linear-gradient(
              145deg,
              rgba(255, 255, 255, 0.35) 0%,
              rgba(255, 255, 255, 0.12) 45%,
              rgba(255, 255, 255, 0.22) 100%
            );
          }
        `;
}

export function glassBorderRingHostClassNames(
	options: Omit<GlassBorderRingClassNamesOptions, "variant"> = {},
): string {
	return glassBorderRingClassNames({ ...options, variant: "host" });
}

export interface GlassBorderRingProps {
	tone?: GlassBorderTone;
	borderWidth?: number;
	borderRadius: string;
	disabled?: boolean;
	className?: string;
}

/**
 * Lightweight ring-only glass band (no backdrop-blur, no layout padding).
 * Prefer {@link GLASS_BORDER_RING_HOST_CLASS} on torch/bento surfaces when a pseudo-element suffices.
 */
export function glassBorderRingElementProps({
	tone = "system",
	borderWidth = GLASS_BORDER_RING_WIDTH_PX,
	borderRadius,
	disabled = false,
	className,
}: GlassBorderRingProps): {
	className: string;
	style: CSSProperties;
	"aria-hidden": true;
} {
	return {
		className: cn(
			glassBorderRingClassNames({ tone, disabled, variant: "element" }),
			className,
		),
		style: {
			borderRadius,
			padding: `${borderWidth}px`,
			...GLASS_BORDER_RING_MASK_STYLE,
		},
		"aria-hidden": true as const,
	};
}
